<template>
  <nav
    v-if="!nonavbar"
    aria-label="main navigation"
    class="navbar is-fixed-top"
    role="navigation"
  >
    <div
      class="navbar-brand"
    >
      <a
        aria-expanded="false"
        aria-label="menu"
        class="navbar-burger burger"
        data-target="navbarMenu"
        role="button"
      >
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </a>
    </div>
    <div
      id="navbarMenu"
      class="navbar-menu"
    >
      <div class="navbar-start">
        <NavbarItem
          v-for="item in navigationItems"
          :key="item.target"
          :item="item"
          @navigate-to="navigateTo"
        />
      </div>

      <div class="navbar-end">
        <VersionInfo/>
      </div>
    </div>
  </nav>
</template>


<script setup lang="ts">
  import {computed, onMounted} from 'vue';
  import {useRoute} from 'vue-router';
  import {Navigation, NavigationItem} from '@/model/site/navigation.model';
  import NavbarItem from '@/components/layout/navigation/NavbarItem.vue';
  import VersionInfo from '@/components/layout/util/VersionInfo.vue';
  import navigationData from '@/assets/static/navigation.json';
  import {log} from '@/utils/logger';

  const navigation: Navigation = navigationData;
  const route = useRoute();

  const nonavbar = computed(() => route.query.nonavbar === '1');

  const navigationItems = computed<NavigationItem[]>(() => {
    const statsItems = navigation.items.find(item => item.id === 'stats-index');
    if (!statsItems || !statsItems.subitems) {
      return [];
    }
    return statsItems.subitems
      .map(item => ({...item, target: item.target.includes('stats') ? item.target : `stats/${item.target}`}))
      .filter(item => !item.disabled);
  });

  function navbar(): HTMLElement | null {
    return document.querySelector('.navbar-burger');
  }

  function toggleNavbar(): void {
    // can not use event.target here: when closing event-source is different
    const burger = navbar();
    if (!burger) {
      log.warn('Could not register navbar click-listener: element [.navbar-burger] not found');
      return;
    }
    const target = burger.dataset.target;
    const $target = target ? document.getElementById(target) : null;
    burger.classList.toggle('is-active');
    if ($target) {
      $target.classList.toggle('is-active');
    } else {
      log.warn('Could not toggle navbar', target);
    }
  }

  function registerNavbar(): void {
    const burger = navbar();
    if (!burger) {
      log.warn('Could not register navbar click listener: element [.navbar-burger] not found');
      return;
    }
    burger.addEventListener('click', toggleNavbar);
  }

  function navigateTo(): void {
    toggleNavbar();
  }

  onMounted(() => {
    if (!nonavbar.value) {
      registerNavbar();
    }
  });
</script>

<style lang="scss">
  @import '../../assets/styles/variables';

  a.navbar-item, a.navbar-link, .navbar-item a {
    &:hover {
      background-color: $primary !important;
      color: $primary-invert !important;
    }
  }

  nav {
    .image {
      max-height: $navbar-height;
      margin: 0.25em;
      // same width as in old design
      width: 161px;
    }

    box-shadow: 0 0 2px rgba(255, 255, 255, .2);
  }

  #nav-toggle-state {
    display: none;
  }

  #nav-toggle-state:checked ~ .nav-menu {
    display: block;
  }

  .navbar {
    color: $text-strong;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    font-weight: bold;
    border-bottom: 2px $hf-border-color solid;
  }

  .navbar,
  .navbar-menu,
  .navbar-end,
  .navbar-start {
    //background: $hf-thead-background;
  }

  .router-link-active {
    border-top: 3px $primary solid;
  }

  .navbar-brand {
    .router-link-active {
      border: 0;
    }
  }


</style>
